import { useUpdateDepartment } from '@hooks/menu';
import { Edit2 } from 'lucide-react';
import { useState } from 'react';

import { useAppSelector } from '../../../hooks/useRedux';
import { ONBOARDING_TOUR_TARGETS } from '../../../services/onboardingTourService';
import { selectCanManageDepartments } from '../../../store/auth/authSelectors';
import { selectEffectiveOutlet } from '../../../store/outlets';
import { Department } from '../../../types/department';
import { UpdateDepartmentModal } from './UpdateDepartmentModal';

interface Props {
  departments: Department[];
}

export const DepartmentsTable = ({ departments }: Props) => {
  const [selectedDepartment, setSelectedDepartment] =
    useState<Department | null>(null);
  const selectedOutlet = useAppSelector(selectEffectiveOutlet);
  const canManageDepartments = useAppSelector(selectCanManageDepartments);
  const updateDepartment = useUpdateDepartment();

  const openEditModal = (department: Department) => {
    if (!canManageDepartments) return;
    setSelectedDepartment(department);
  };

  const handleToggleKot = (department: Department) => {
    if (!canManageDepartments) return;
    updateDepartment.mutate({
      id: department.id,
      kot_enabled: !department.kot_enabled,
    });
  };

  return (
    <>
      <div
        className="overflow-hidden rounded-xl border border-gray-200 bg-white"
        data-onboarding-tour={ONBOARDING_TOUR_TARGETS.departmentsTable}
      >
        <div className="grid grid-cols-12 gap-3 border-b border-gray-200 bg-gray-50 px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-500">
          <div className="col-span-5">Name</div>
          <div className="col-span-3">Outlet</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        <div className="divide-y divide-gray-100">
          {departments.map((department) => {
            const isTogglingThis =
              updateDepartment.isPending &&
              updateDepartment.variables?.id === department.id &&
              updateDepartment.variables?.kot_enabled !== undefined;

            return (
              <div
                key={department.id}
                className="grid grid-cols-12 items-center gap-3 px-4 py-2.5 transition-colors hover:bg-gray-50"
              >
                <div className="col-span-5 truncate text-sm font-medium text-gray-900">
                  {department.name}
                </div>
                <div className="col-span-3 truncate text-sm text-gray-600">
                  {department.outlet_name || selectedOutlet?.name || '—'}
                </div>
                <div className="col-span-2">
                  <span
                    className={`inline-flex items-center rounded px-2 py-0.5 text-[10px] font-bold uppercase ${
                      department.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {department.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="col-span-2 flex items-center justify-end gap-3">
                  <label
                    className={`flex items-center gap-1.5 ${
                      canManageDepartments
                        ? 'cursor-pointer'
                        : 'cursor-not-allowed opacity-60'
                    }`}
                    title="Print KOT for this department"
                  >
                    <span className="text-[10px] font-semibold uppercase text-gray-400">
                      KOT
                    </span>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={department.kot_enabled}
                        disabled={!canManageDepartments || isTogglingThis}
                        onChange={() => handleToggleKot(department)}
                        className="sr-only peer"
                      />
                      <div className="h-4 w-8 rounded-full bg-gray-200 transition-colors peer-checked:bg-pos-primary" />
                      <div className="absolute left-0.5 top-0.5 h-3 w-3 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4" />
                    </div>
                  </label>
                  {canManageDepartments ? (
                    <button
                      type="button"
                      onClick={() => openEditModal(department)}
                      className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                    >
                      <Edit2 size={14} />
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Form Modal */}
      <UpdateDepartmentModal
        isOpen={selectedDepartment !== null}
        onClose={() => setSelectedDepartment(null)}
        department={selectedDepartment}
      />
    </>
  );
};
